import {
  GoogleMap,
  Marker,
  LoadScriptNext,
  Polygon,
  Polyline,
} from "@react-google-maps/api";

import React from "react";
import { MapKey } from "../../../Data/Data";

const HeatMapContainer = ({
  api,
  position,
  setPosition,
  mapRef,
  setMapRef,
  isTigerNetworkChecked,
  markers,
  isYearlyTigerChecked,
  handleMarkerDragEnd,
}) => {
  const handleMapLoad = (map) => {
    setPosition({
      lat: map.latLng.lat(),
      lng: map.latLng.lng(),
    });
  };

  const onLoad = (map) => {
    setMapRef(map);
  };

  const handleDragEnd = () => {
    if (mapRef) {
      const newCenter = mapRef.getCenter();
      setPosition({
        lat: newCenter.lat(),
        lng: newCenter.lng(),
      });
    }
  };

  const latestYear = Math.max(
    ...markers.flatMap((marker) =>
      marker.dates?.map((date) => new Date(date).getFullYear())
    )
  );

  const latestYearMarkers = markers
    .flatMap((marker) =>
      marker.dates
        .filter((date) => new Date(date).getFullYear() === latestYear)
        .map((date) => ({
          date,
          lat: marker.lat,
          lng: marker.lng,
        }))
    )
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .map((item, index) => ({
      rank: index + 1,
      ...item,
    }));

  function createColoredMarker(color, countOrRank) {
    return `data:image/svg+xml;charset=UTF-8,
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="10" fill="${color}" />
          <text x="20" y="20" text-anchor="middle" dy=".3em" font-family="Arial" font-size="12" fill="black">
            ${isTigerNetworkChecked ? countOrRank : countOrRank}
          </text>
      </svg>`;
  }

  const getColorByYear = (year) => {
    const colorMap = {
      2025: "orange",
      2024: "blue",
      2023: "green",
      2022: "red",
      2021: "yellow",
    };
    return colorMap[year] || "gray";
  };

  function getColor(count, maxCount, dates) {
    if (isYearlyTigerChecked) {
      const year = new Date(dates[0]).getFullYear();
      return getColorByYear(year);
    } else {
      const normalized = count / maxCount;
      const r = 255;
      const g = Math.round(255 * (1 - normalized));
      const b = 0;
      return `rgb(${r}, ${g}, ${b})`;
    }
  }

  return (
    <div className="Map-View">
      <LoadScriptNext googleMapsApiKey={MapKey}>
        <GoogleMap
          mapContainerStyle={{ width: "95%", height: "100%" }}
          center={
            position === null ? { lat: 21.572004, lng: 79.19164 } : position
          }
          zoom={11}
          onClick={handleMapLoad}
          onLoad={onLoad}
          onDragEnd={handleDragEnd}
        >
          <Polygon
            paths={api.polygonCoordinates}
            options={{
              fillColor: "skyblue",
              fillOpacity: 0.4,
              strokeColor: "blue",
              strokeOpacity: 0.6,
              strokeWeight: 2,
            }}
            onClick={handleMapLoad}
          />

          {isTigerNetworkChecked ? (
            <>
              {latestYearMarkers.map((marker, index) => {
                const offset = 0.001 * index;
                const iconUrl = createColoredMarker("violet", marker.rank);
                if (!window.google || !window.google.maps) return null;
                return (
                  <Marker
                    key={index}
                    position={{
                      lat: marker.lat + offset,
                      lng: marker.lng + offset,
                    }}
                    title={`Rank: ${marker.rank} Date: ${new Date(
                      marker.date
                    ).toLocaleDateString("en-GB")}`}
                    icon={{
                      url: iconUrl,
                      scaledSize: new window.google.maps.Size(40, 40),
                    }}
                    draggable={true}
                    onDragEnd={handleMarkerDragEnd}
                  />
                );
              })}

              {/* <Polyline
                          path={latestYearMarkers.map((marker) => ({
                            lat: marker.lat,
                            lng: marker.lng,
                          }))}
                          options={{
                            strokeColor: "red",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            geodesic: true,
                          }}
                        /> */}
            </>
          ) : (
            markers.flatMap((marker, index) => {
              const yearGroups = marker.dates?.reduce((acc, date) => {
                const year = new Date(date).getFullYear();
                if (!acc[year]) {
                  acc[year] = 0;
                }
                acc[year] += 1;
                return acc;
              }, {});
              const years = Object.keys(yearGroups);
              const hasDifferentYears = years.length > 1;

              if (
                marker.count > 1 &&
                isYearlyTigerChecked &&
                hasDifferentYears
              ) {
                const offset = 0.001;
                return years.map((year, yearIndex) => {
                  const color = getColorByYear(year);
                  const yearCount = yearGroups[year];
                  const iconUrl = createColoredMarker(color, yearCount);
                  if (!window.google || !window.google.maps) return null;
                  return (
                    <Marker
                      key={`${index}-${year}`}
                      position={{
                        lat: marker.lat + offset * yearIndex,
                        lng: marker.lng + offset * yearIndex,
                      }}
                      title={`Station Id: ${marker.label} Year: ${year} Total images: ${yearCount}`}
                      icon={{
                        url: iconUrl,
                        scaledSize: new window.google.maps.Size(40, 40),
                      }}
                      draggable={true}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  );
                });
              } else {
                const duplicateMarkers = markers.filter(
                  (m) => m.lat === marker.lat && m.lng === marker.lng
                );
                const offset = duplicateMarkers.length > 1 ? 0.0001 * index : 0;
                const maxCount = markers.reduce(
                  (max, marker) => Math.max(max, marker.count),
                  0
                );
                const color = getColor(marker.count, maxCount, marker.dates);
                const iconUrl = createColoredMarker(color, marker.count);
                if (!window.google || !window.google.maps) return null;
                return (
                  <Marker
                    key={index}
                    position={{
                      lat: marker.lat + offset,
                      lng: marker.lng + offset,
                    }}
                    title={`Station Id: ${marker.label} Total images: ${marker.count}`}
                    icon={{
                      url: iconUrl,
                      scaledSize: new window.google.maps.Size(40, 40),
                    }}
                    draggable={true}
                    onDragEnd={handleMarkerDragEnd}
                  />
                );
              }
            })
          )}
        </GoogleMap>
      </LoadScriptNext>
    </div>
  );
};

export default HeatMapContainer;
